import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';
import { apiClient } from '@/api/axios';
import { API } from '@/api/endpoints';
import { FIRST_PAGE, getNextPageFromMeta } from '@/api/pagination';
import { STALE_TIME } from '@/api/staleTimes';
import { downloadBlob, openBlobInNewTab } from '@/utils/download';
import { DEFAULT_PUBLISH_PAGE_SIZE, type PublishStage } from '../constants/publish.constants';
import {
  batchMemberListSchema,
  batchResponseSchema,
  candidateListSchema,
  type Batch,
  type BatchMember,
  type BatchResponse,
  type BatchSearch,
  type Candidate,
  type PublishPayload,
} from '../types/publish.types';

export const publishQueryKeys = {
  all: () => ['publish'] as const,
  candidates: (dateFrom: string | undefined, dateTo: string | undefined) =>
    [...publishQueryKeys.all(), 'candidates', dateFrom, dateTo] as const,
  batches: (stage: PublishStage, params: BatchSearch) =>
    [...publishQueryKeys.all(), 'batches', stage, params] as const,
  members: (batchId: number | undefined) =>
    [...publishQueryKeys.all(), 'members', batchId] as const,
  batch: (batchId: number) => [...publishQueryKeys.all(), 'batch', batchId] as const,
};

const SINGLE_BATCH_PAGE_SIZE = 1;
const ANY_STAGE: PublishStage = 'all';

export const candidatesQueryOptions = (dateFrom: string | undefined, dateTo: string | undefined) =>
  queryOptions({
    queryKey: publishQueryKeys.candidates(dateFrom, dateTo),
    queryFn: async (): Promise<Candidate[]> => {
      const res = await apiClient.get(API.publish.candidates, {
        params: { dateFrom, dateTo },
      });
      return candidateListSchema.parse(res.data);
    },
    enabled: dateFrom !== undefined && dateTo !== undefined,
    staleTime: STALE_TIME,
  });

export const batchesInfiniteQueryOptions = (stage: PublishStage, params: BatchSearch) =>
  infiniteQueryOptions({
    queryKey: publishQueryKeys.batches(stage, params),
    queryFn: async ({ pageParam }): Promise<BatchResponse> => {
      const res = await apiClient.get(API.publish.batches, {
        params: { ...params, stage, page: pageParam, pageSize: DEFAULT_PUBLISH_PAGE_SIZE },
      });
      return batchResponseSchema.parse(res.data);
    },
    initialPageParam: FIRST_PAGE,
    getNextPageParam: getNextPageFromMeta,
    staleTime: STALE_TIME,
  });

export const batchQueryOptions = (batchId: number) =>
  queryOptions({
    queryKey: publishQueryKeys.batch(batchId),
    queryFn: async (): Promise<Batch | null> => {
      const res = await apiClient.get(API.publish.batches, {
        params: { stage: ANY_STAGE, batchId, page: FIRST_PAGE, pageSize: SINGLE_BATCH_PAGE_SIZE },
      });
      return batchResponseSchema.parse(res.data).data[0] ?? null;
    },
    staleTime: STALE_TIME,
  });

export const batchMembersQueryOptions = (batchId: number | undefined) =>
  queryOptions({
    queryKey: publishQueryKeys.members(batchId),
    queryFn: async (): Promise<BatchMember[]> => {
      const res = await apiClient.get(API.publish.batchMembers(batchId ?? 0));
      return batchMemberListSchema.parse(res.data);
    },
    enabled: batchId !== undefined,
    staleTime: STALE_TIME,
  });

export async function publishBatch(payload: PublishPayload): Promise<Batch> {
  const res = await apiClient.post(API.publish.batches, payload);
  return res.data as Batch;
}

export async function confirmBatch(
  batchId: number,
  isConfirm: boolean,
  comments?: string,
): Promise<void> {
  await apiClient.patch(API.publish.confirm(batchId), {
    isConfirm,
    ...(comments === undefined ? {} : { comments }),
  });
}

export async function disburseBatch(batchId: number, comments?: string): Promise<void> {
  await apiClient.patch(API.publish.disburse(batchId), {
    ...(comments === undefined ? {} : { comments }),
  });
}

export async function exportBatchMembers(batchId: number, publishNo: number): Promise<void> {
  const res = await apiClient.get(API.publish.batchMembersExport(batchId), {
    responseType: 'blob',
  });
  downloadBlob(res.data as Blob, `${publishNo}-PublishedList.xlsx`);
}

export async function openMemberBill(batchId: number, userId: number): Promise<void> {
  const res = await apiClient.get(API.publish.memberBill(batchId, userId), {
    responseType: 'blob',
  });
  openBlobInNewTab(res.data as Blob);
}

export async function openCandidateBill(
  userId: number,
  dateFrom: string,
  dateTo: string,
): Promise<void> {
  const res = await apiClient.get(API.publish.bill, {
    params: { userId, dateFrom, dateTo },
    responseType: 'blob',
  });
  openBlobInNewTab(res.data as Blob);
}

export async function downloadBatchBills(batchId: number, publishNo: number): Promise<void> {
  const res = await apiClient.get(API.publish.batchBills(batchId), {
    responseType: 'blob',
  });
  downloadBlob(res.data as Blob, `${publishNo}_AllBills.html`);
}
